import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {useMutation, useQuery} from "react-query";
import Stepper, { EStepState } from "@components/new/Stepper";
import { ButtonType } from "@components/new/Button";
import { Modal, ModalSize } from "@components/new/Modal";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import ConfirmModal from "@components/modals/ConfirmModal";
import {
  getFBPStocks,
  removeMatchingSeller,
  getBlockedWarehouses,
} from "@api/apiClient/fbp";
import Form from "./LinkForm";
import BlockingReasons from "./BlockingReasons";
import { FormHandle } from "./types";
import AlertIcon from "@assets/icons/solid/circle-alert.svg?react";
import styles from "./Form.module.scss";

type TBlockedWarehousesData = Awaited<ReturnType<typeof getBlockedWarehouses>>;

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
}

interface DelLinkModalProps {
  onClose: () => void;
  sellerWarehouseId: string;
  onError: () => void;
  onSuccess: () => void;
  stores: string[];
}

enum steps {
  INIT = "init",
  FORM = "form",
  SUCCESS = "success",
  ERROR = "error",
  CONFIRM = "confirm",
  REASONS = "reasons",
}

const hasDeletionBlock = (data?: TBlockedWarehousesData) => Boolean(
  data?.isDeletionBlocked ||
  data?.ymWarehouses?.some(v => v.isDeletionBlocked) ||
  data?.wbWarehouses?.some(v => v.isDeletionBlocked)
);

const DelLinkModal = ({
  onClose,
  sellerWarehouseId,
  // onError: onDelError,
  onSuccess: onDelSuccess,
  stores,
}: DelLinkModalProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(steps.INIT);
  const [ buttonProps, setButtonProps ] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
  });
  const [ headerMessage, setHeaderMessage ] = useState<React.ReactNode>();
  const formRef = useRef<FormHandle>({ submit: () => { } });

  const removeLinkMutation = useMutation({
    mutationFn: () => removeMatchingSeller(sellerWarehouseId),
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  const blockedWarehousesQuery = useQuery({
    queryKey: ["getBlockedWarehouses", sellerWarehouseId],
    queryFn: () => getBlockedWarehouses(sellerWarehouseId),
    retry: 1,
    onSuccess: (data) => {
      if (data.isDeletionBlocked || data.ymWarehouses?.find(v => v.isDeletionBlocked) || data.wbWarehouses?.find(v => v.isDeletionBlocked)) {
        setStep(steps.REASONS)
      };
    },
    select: (data) => ({
      ...data,
      ymWarehouses: data.ymWarehouses.filter(v => v.isDeletionBlocked),
      wbWarehouses: data.wbWarehouses.filter(v => v.isDeletionBlocked),
    }),
    enabled: Boolean(sellerWarehouseId),
  });

  const isDeletionBlocked = hasDeletionBlock(blockedWarehousesQuery.data);

  const stocksQuery = useQuery({
    queryKey: ["getFBPStocks", sellerWarehouseId, "total", "hasStock"],
    queryFn: () => getFBPStocks({
      filters: [],
      page: { page: 1, pageSize: 1 },
      sellerWarehouseId,
      hasStock: true,
    }),
    retry: 1,
    enabled: Boolean(sellerWarehouseId) && blockedWarehousesQuery.isFetched && !isDeletionBlocked,
  });

  useEffect(() => {
    if (!sellerWarehouseId) {
      setStep(steps.ERROR);
    }
  }, [sellerWarehouseId]);

  useEffect(() => {
    if (!blockedWarehousesQuery.isFetched) return;

    if (hasDeletionBlock(blockedWarehousesQuery.data)) {
      setStep(steps.REASONS);
    }
  }, [blockedWarehousesQuery.data, blockedWarehousesQuery.isFetched]);

  useEffect(() => {
    if (!stocksQuery.isFetched || isDeletionBlocked) return;

    setStep((currentStep) => {
      if (currentStep === steps.REASONS) return currentStep;

      return !stocksQuery.data?.items?.length ? steps.CONFIRM : steps.FORM;
    });
  }, [isDeletionBlocked, stocksQuery.data, stocksQuery.isFetched]);

  useEffect(() => {
    if (blockedWarehousesQuery.isError || stocksQuery.isError) {
      setStep(steps.ERROR);
    }
  }, [blockedWarehousesQuery.isError, stocksQuery.isError]);

  const handleStatusChange = (props: ISubmitButtonProps, msg: React.ReactNode) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
    });
    setHeaderMessage(msg);
  }

  const onSuccess = () => {
    setStep(steps.SUCCESS);
    // stocksQuery.refetch();
  }

  const onError = () => {
    setStep(steps.ERROR);
  }

  const stepsData = [
    { text: t('fbpPage.delStoreModal.step-1'), state: step === steps.FORM ? EStepState.ENABLED : EStepState.SUCCESS },
    {
      text: t('fbpPage.delStoreModal.step-2'),
      state: (step === steps.SUCCESS ? EStepState.SUCCESS : (
        step === steps.ERROR ? EStepState.ERROR : EStepState.DISABLED
      )),
    },
  ]

  if (step === steps.INIT) return null

  if (step === steps.CONFIRM) {
    return (
      <ConfirmModal
        title={t("fbpPage.delStoreModal.confirmLinkTitle")}
        description={t("fbpPage.delStoreModal.confirmLinkDescription")}
        noBtnTitle={t("fbpPage.delStoreModal.cancelBtn")}
        yesBtnTitle={t("fbpPage.delStoreModal.delBtn")}
        icon={<AlertIcon />}
        onClose={onClose}
        requestFn={() => {
          removeLinkMutation.mutate();
        }}
        onSuccess={() => { onDelSuccess() }}
        type="error"
        inModal
        yesBtnType={ButtonType.ATTENTION}
      />
    );
  }

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.delStoreModal.titleLink')}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.delStoreModal.closeBtn'),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          className={styles.successContent}
          descriptionClassName={styles.successDescription}
          title={t('fbpPage.delStoreModal.successTitle')}
          description={t('fbpPage.delStoreModal.successDescription')}
          variant="success"
        />
      </Modal>
    )
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.delStoreModal.titleLink')}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.delStoreModal.closeBtn'),
            onClick: onClose,
          },
          {
            title: t('fbpPage.delStoreModal.repeatBtn'),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.delStoreModal.errorTitle')}
          description={t('fbpPage.delStoreModal.errorDescription')}
          variant="warning"
        />
      </Modal>
    )
  }

  if (step === steps.REASONS) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.delStoreModal.titleLink')}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.delStoreModal.closeBtn'),
            onClick: onClose,
          },
        ]}
      >
        <BlockingReasons
          warehouses={[
            ...(blockedWarehousesQuery.data?.wbWarehouses ?? []).map(v => ({
              id: v.id,
              name: v.name,
              blockingReasons: v.blockingReasons,
              marketplace: "Wildberries",
            })),
            ...(blockedWarehousesQuery.data?.ymWarehouses ?? []).map(v => ({
              id: v.id,
              name: v.name,
              blockingReasons: v.blockingReasons,
              marketplace: "Yandex",
            })),
          ]}
        />
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={true}
      title={t('fbpPage.delStoreModal.titleLink')}
      headerContent={<Stepper steps={stepsData} gap="small" />}
      headerTitleContent={headerMessage}
      size={ModalSize.L}
      onClose={onClose}
      confirmCancel
      confirmCancelDescription={t('fbpPage.delStoreModal.closeConfirmDescription')}
      footerButtons={[
        {
          title: t('fbpPage.delStoreModal.closeBtn'),
        },
        {
          title: t('fbpPage.delStoreModal.nextBtn'),
          onClick: () => {
            formRef.current && formRef.current.submit();
          },
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading,
        },
      ]}
    >
      <Form
        formRef={formRef}
        sellerWarehouseId={sellerWarehouseId}
        onStatusChange={handleStatusChange}
        onSuccess={onSuccess}
        onError={onError}
        stores={stores}
      />
    </Modal>
  )
}

export default DelLinkModal;
